
/* injects from baggage-loader */

import React from 'react'
import PropTypes from 'prop-types'
import {connect} from 'react-redux'
import {
  fetchOrders,
  changeOrdersSort,
  changeSelectedMetric
} from 'redux/modules/insights/actions'
import {setTimePeriod, setConversionType} from 'redux/modules/currentUser/actions'
import {getOrders, getSelectedMetric, getMetrics} from 'redux/modules/insights/selectors'
import {
  getCurrentTimePeriod,
  getTimePeriodRanges,
  getAggregatedSites
} from 'redux/modules/currentUser/selectors'
import isRangeEqual from 'utils/isRangeEqual'
import OverviewTable from 'components/OverviewTable'
import ColorLegend from 'components/ColorLegend'
import formatTimePeriodDate from 'utils/formatTimePeriodDate'
import isEqual from 'lodash/isEqual'
import {getSelectedDateRange, getConverionType} from 'redux/modules/currentUser/selectors'

export class OrdersOverviewOrderCountView extends React.Component {
  static propTypes = {
    today: PropTypes.instanceOf(Date),
    data: PropTypes.array,
    fetchOrders: PropTypes.func,
    setTimePeriod: PropTypes.func,
    changeOrdersSort: PropTypes.func,
    timePeriod: PropTypes.string,
    dynamicDateType: PropTypes.string,
    timePeriodRanges: PropTypes.object,
    sortKey: PropTypes.string,
    sortDirection: PropTypes.string,
    sites: PropTypes.oneOfType([PropTypes.string, PropTypes.array]),
    isFetching: PropTypes.bool,
    selectedMetric: PropTypes.string,
    changeSelectedMetric: PropTypes.func,
    metrics: PropTypes.object,
    crossSiteMode: PropTypes.bool,
    setConversionType: PropTypes.func,
    conversionType: PropTypes.string,
  }

  static contextTypes = {
    currency: PropTypes.string,
    timezone: PropTypes.string
  }

  componentDidMount() {
    const {fetchOrders, selectedMetric, changeSelectedMetric, crossSiteMode, conversionType} = this.props
    const {currency, timezone} = this.context
    fetchOrders(selectedMetric, conversionType, crossSiteMode, currency, timezone)
    changeSelectedMetric(selectedMetric)
  }

  componentWillReceiveProps(nextProps, nextContext) {
    const {currency, timezone} = nextContext
    const {fetchOrders, sites, timePeriod, timePeriodRanges, selectedMetric, conversionType} = this.props
    const {timePeriodRanges: nextTimePeriodRanges, currentMonthDataFetching, crossSiteMode} = nextProps
    const sitesChanged = !isEqual(sites, nextProps.sites)

    if (!nextTimePeriodRanges || !timePeriodRanges) return null

    const timePeriodChanged = timePeriod !== nextProps.timePeriod
    const timeRangesValid = !!nextTimePeriodRanges.start && !!nextTimePeriodRanges.end
    const metricChanged = selectedMetric !== nextProps.selectedMetric
    const conversionTypeChanged = conversionType !== nextProps.conversionType
    if (!timeRangesValid) return null

    const timeRangesChanged = !(
      isRangeEqual(timePeriodRanges.start, nextTimePeriodRanges.start) &&
      isRangeEqual(timePeriodRanges.end, nextTimePeriodRanges.end)
    )
    if (
      !currentMonthDataFetching &&
      (metricChanged || sitesChanged || timePeriodChanged || timeRangesChanged || conversionTypeChanged)
    ) {
      fetchOrders(nextProps.selectedMetric, nextProps.conversionType, crossSiteMode, currency, timezone)
    }
  }

  render() {
    const {
      timePeriod,
      dynamicDateType,
      timePeriodRanges,
      data,
      sortKey,
      today,
      sortDirection,
      isFetching,
      setTimePeriod,
      changeOrdersSort,
      selectedMetric,
      metrics,
      conversionType,
      setConversionType
    } = this.props

    if (!timePeriodRanges) return null

    const currentPeriodName = formatTimePeriodDate(timePeriod, timePeriodRanges.start.start)
    const anotherPeriodName = formatTimePeriodDate(timePeriod, timePeriodRanges.end.start)
    const {label} = metrics[selectedMetric]
    const columns = [
      {
        type: 'chart',
        label: (
          <div className='table-cell is-grow'>
            {label}
            <ColorLegend
              title={`These graphs show comparison between ${currentPeriodName} and ${anotherPeriodName}.`}
              visible
            />
          </div>
        ),
        width: 'grow',
        dataKey: 'orderCount',
        accumulatedValues: true
      },
      {
        type: 'value',
        headerKey: 'orderCountSum',
        label: 'Amount',
        description: currentPeriodName,
        width: '100px',
        dataKey: 'orderCountSum',
        format: 'decimalDouble',
        applyColor: false
      },
      {
        type: 'value',
        headerKey: 'orderCountAbsoluteSumChange',
        label: 'Change',
        description: `comp ${anotherPeriodName}`,
        width: '100px',
        dataKey: 'orderCountAbsoluteSumChange',
        format: 'signed-decimalDouble',
        applyColor: true
      },
      {
        type: 'value',
        headerKey: 'orderCountRelativeSumChange',
        label: '% change',
        description: `comp ${anotherPeriodName}`,
        width: '100px',
        dataKey: 'orderCountRelativeSumChange',
        format: 'signed-percent',
        applyColor: true
      }
    ]

    return (
      <div className='orders-overview-view'>
        <OverviewTable
          timePeriod={timePeriod}
          timePeriodRanges={timePeriodRanges}
          data={data}
          isLoading={isFetching}
          disabled={isFetching}
          sortKey={sortKey}
          sortDirection={sortDirection}
          today={today}
          setTimePeriod={setTimePeriod}
          dynamicDateType={dynamicDateType}
          changeSort={changeOrdersSort}
          columns={columns}
          selectedMetric={selectedMetric}
          metrics={metrics}
          onMetricChange={this.onMetricChange}
          conversionType={conversionType}
          setConversionType={setConversionType}
        />
      </div>
    )
  }

  onMetricChange = item => {
    const {changeSelectedMetric} = this.props
    changeSelectedMetric(item.value)
  }
}

const mapStateToProps = (state, props) => {
  const {metric} = props.route
  const {
    isFetching,
    sortDirection,
    sortKey,
    selectedMetric
  } = state.insights.orders
  const timePeriod = getCurrentTimePeriod(state)
  const timePeriodRanges = getTimePeriodRanges(state)
  const metrics = getMetrics(metric)
  const crossSiteMode = props.route.crossSiteMode
  const {dynamicDateType} = getSelectedDateRange(state)
  const conversionType = getConverionType(state)

  return {
    today: state.today,
    timePeriod,
    dynamicDateType,
    timePeriodRanges,
    sites: getAggregatedSites(state, crossSiteMode),
    data: getOrders(state, timePeriodRanges, selectedMetric),
    isFetching,
    sortDirection,
    sortKey,
    selectedMetric: getSelectedMetric(selectedMetric, Object.keys(metrics)),
    metrics,
    crossSiteMode,
    conversionType
  }
}

const mapDispatchToProps = {
  fetchOrders,
  setTimePeriod,
  setConversionType,
  changeOrdersSort,
  changeSelectedMetric
}

export default connect(mapStateToProps, mapDispatchToProps)(OrdersOverviewOrderCountView)



// WEBPACK FOOTER //
// ./client/views/OrdersOverviewView/index.js